import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  BarChart3,
  ChevronDown,
  Download,
  FileText,
  LayoutDashboard,
  LogOut,
  Menu,
  X,
  Palette,
  Users as UsersIcon,
  Layers,
  UserCircle,
  ScanLine,
  Megaphone,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useAuth } from "../auth/AuthContext.js";
import { GlobalSearch } from "../search/GlobalSearch.js";
import { AppFooter } from "./AppFooter.js";
import { BrandMark } from "./BrandMark.js";

/** The sidebar's destinations, plus "profile" — reachable only from the user menu, not the
 *  sidebar, since it's about the signed-in account rather than a section of the platform. */
export type ShellView =
  | "home"
  | "forms"
  | "events"
  | "broadcasts"
  | "analytics"
  | "exports"
  | "brand"
  | "programmes"
  | "users"
  | "profile";

const NAV_ITEMS: { view: ShellView; label: string; icon: LucideIcon; superadminOnly?: boolean }[] =
  [
    { view: "home", label: "Dashboard", icon: LayoutDashboard },
    { view: "forms", label: "Forms", icon: FileText },
    // Sits next to Forms because an event belongs to one. Always visible: the API gates the
    // actions, and hiding the section from a reviewer who is on the door that afternoon would
    // hide the one screen they came for.
    { view: "events", label: "Events", icon: ScanLine },
    { view: "broadcasts", label: "Broadcasts", icon: Megaphone },
    { view: "analytics", label: "Analytics", icon: BarChart3 },
    { view: "exports", label: "Exports", icon: Download },
    { view: "programmes", label: "Programmes", icon: Layers },
    // Brand assets (logos, colours) are platform-wide, not per-form — the same superadmin gate as
    // /brand-profiles on the API side. Users is superadmin-only on the API too (usersRouter.use
    // (requireAuth(), requireSuperadmin())) — hiding it here is a UX nicety, not the real gate.
    { view: "brand", label: "Brand", icon: Palette, superadminOnly: true },
    { view: "users", label: "Users", icon: UsersIcon, superadminOnly: true },
  ];

const initials = (name: string | undefined): string =>
  (name ?? "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "?";

/** The user menu — avatar initials, name/email, profile, sign out. Closes on an outside click or Escape. */
const UserMenu = ({ onOpenProfile }: { onOpenProfile: () => void }): JSX.Element => {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent): void => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("mousedown", onPointerDown);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onPointerDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted"
      >
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">
          {initials(user?.fullName)}
        </span>
        <span className="hidden text-left sm:block">
          <span className="block font-medium leading-tight">{user?.fullName}</span>
          <span className="block text-xs leading-tight text-muted-foreground">{user?.email}</span>
        </span>
        <ChevronDown aria-hidden className="h-3.5 w-3.5 text-muted-foreground" />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-30 mt-1 w-48 rounded-md border bg-popover p-1 shadow-md"
        >
          <div className="border-b px-2 py-1.5 sm:hidden">
            <p className="truncate text-sm font-medium">{user?.fullName}</p>
            <p className="truncate text-xs text-muted-foreground">{user?.email}</p>
          </div>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onOpenProfile();
            }}
            className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-muted"
          >
            <UserCircle aria-hidden className="h-3.5 w-3.5" />
            My profile
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => void logout()}
            className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-muted"
          >
            <LogOut aria-hidden className="h-3.5 w-3.5" />
            Sign out
          </button>
        </div>
      )}
    </div>
  );
};

/**
 * The application shell — a collapsible icon sidebar and a topbar, in the AdminLTE layout family:
 * https://adminlte.io. Brand colours and every surface come from tailwind.config.ts's semantic
 * tokens (sourced from seplatenergy.com), not from AdminLTE's own palette or CSS.
 */
export const AppShell = ({
  children,
  activeView,
  onNavigate,
  onOpenSubmission,
}: {
  children: ReactNode;
  activeView?: ShellView;
  onNavigate?: (view: ShellView) => void;
  onOpenSubmission?: (submissionId: string) => void;
}): JSX.Element => {
  const { user } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const items = NAV_ITEMS.filter((item) => !item.superadminOnly || user?.isSuperadmin);
  const activeLabel = items.find((item) => item.view === activeView)?.label ?? "Dashboard";

  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setDrawerOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drawerOpen]);

  return (
    <div className="flex min-h-screen bg-muted/20">
      {/* The backdrop is the only thing that says "this is a layer over the page" on a phone, and
          the only large tap target for dismissing it. */}
      {drawerOpen && (
        <div
          aria-hidden
          onClick={() => setDrawerOpen(false)}
          className="fixed inset-0 z-30 bg-black/40 lg:hidden"
        />
      )}

      {/*
        One sidebar, two behaviours. From `lg` it is a static rail that collapses to icons; below
        that it slides in over the page, because 224px of a 360px phone screen is most of the
        screen given to navigation the reader is not currently using.

        `invisible` when closed, not merely translated off-screen: a drawer parked at -100% is
        still in the tab order and still read aloud, so a keyboard user tabs into a menu nobody
        can see. `visibility` also transitions discretely, so the slide still animates.
      */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 flex w-64 flex-col border-r bg-card transition-transform duration-200 lg:visible lg:static lg:translate-x-0 lg:transition-[width] ${
          drawerOpen ? "translate-x-0" : "invisible -translate-x-full"
        } ${collapsed ? "lg:w-16" : "lg:w-56"}`}
      >
        {/* The lockup alone. At h-8 the two marks come to ~125px, and the platform name beside
            them wanted another 150 in a 200px sidebar — so the images overflowed the box flexbox
            had shrunk them into and painted over the text. The name now sits in the breadcrumb and
            the footer, where there is room for it, and `compact` drops to a single mark for the
            48px the collapsed rail leaves. */}
        <div
          className={`flex h-14 shrink-0 items-center justify-between gap-2 overflow-hidden border-b px-3 ${
            collapsed ? "lg:justify-center lg:px-2" : ""
          }`}
        >
          {/* The height has to be stated unconditionally: below `lg` the rail is never collapsed,
              and a bare `lg:h-6` would leave the image with no height at all on a phone — which
              renders it at its natural 484px. */}
          <BrandMark className={collapsed ? "h-8 lg:h-6" : "h-8"} compact={collapsed} />
          <button
            type="button"
            onClick={() => setDrawerOpen(false)}
            aria-label="Close menu"
            className="-mr-1 rounded-md p-1.5 text-muted-foreground hover:bg-muted lg:hidden"
          >
            <X aria-hidden className="h-4 w-4" />
          </button>
        </div>

        <nav className="flex flex-1 flex-col gap-0.5 p-2" aria-label="Main">
          {items.map(({ view, label, icon: Icon }) => (
            <button
              key={view}
              type="button"
              onClick={() => {
                setDrawerOpen(false);
                onNavigate?.(view);
              }}
              aria-current={activeView === view ? "page" : undefined}
              title={collapsed ? label : undefined}
              className={`flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                activeView === view
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground"
              }`}
            >
              <Icon aria-hidden className="h-4 w-4 shrink-0" />
              {/* Hidden by CSS rather than unmounted, because `collapsed` describes the desktop
                  rail only — dropping the label outright would leave the phone drawer showing
                  seven unlabelled icons whenever someone had collapsed the rail earlier. */}
              <span className={`truncate ${collapsed ? "lg:hidden" : ""}`}>{label}</span>
            </button>
          ))}
        </nav>
      </aside>

      <div className="flex flex-1 flex-col">
        <header className="flex h-14 items-center gap-3 border-b bg-card px-4">
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            aria-label="Open menu"
            aria-expanded={drawerOpen}
            className="rounded-md p-2 text-muted-foreground hover:bg-muted lg:hidden"
          >
            <Menu aria-hidden className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={() => setCollapsed((v) => !v)}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            className="hidden rounded-md p-1.5 text-muted-foreground hover:bg-muted lg:block"
          >
            <Menu aria-hidden className="h-4 w-4" />
          </button>

          {/* Breadcrumb — just the current section for now; screens opened through a section
              (a form's builder, an analytics drill-down) push their own back button instead. */}
          <nav aria-label="Breadcrumb" className="hidden text-sm text-muted-foreground md:block">
            <span>Seplat Growth Academy</span>
            <span aria-hidden className="mx-1.5">
              /
            </span>
            <span className="font-medium text-foreground">{activeLabel}</span>
          </nav>

          <div className="ml-auto flex items-center gap-3">
            {/* One search box, in the header, across every form and cycle the user can see (§9.2). */}
            <GlobalSearch onOpenSubmission={(id) => onOpenSubmission?.(id)} />
            <UserMenu onOpenProfile={() => onNavigate?.("profile")} />
          </div>
        </header>

        <main className="flex min-h-0 flex-1 flex-col p-4 sm:p-6">{children}</main>

        <AppFooter />
      </div>
    </div>
  );
};
